import { useCallback, useContext, useEffect, useState } from 'react';
import { NodeProps } from 'reactflow';
import { Button } from '@givebutter/primitives/button';
import { faSplit, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    BranchConditionNodeData,
    BranchFormValues,
    BranchNodeData,
} from 'assets/js/pages/engage/workflows/actions/types';
import { BranchCampaignsDisplayValue } from 'assets/js/pages/engage/workflows/components/BranchCampaignsDisplayValue';
import * as BranchConditionItem from 'assets/js/pages/engage/workflows/components/BranchConditionItem';
import { BranchConditionOptionLabel } from 'assets/js/pages/engage/workflows/components/BranchConditionOptionLabel';
import { BranchFormValueInput } from 'assets/js/pages/engage/workflows/components/BranchFormValueInput';
import { BranchMessageDisplayValue } from 'assets/js/pages/engage/workflows/components/BranchMessageDisplayValue';
import { BranchTagsDisplayValue } from 'assets/js/pages/engage/workflows/components/BranchTagsDisplayValue';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import { DeleteCanvasNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DeleteCanvasNodeMenuItem';
import {
    ParentMessageContext,
    ParentMessageProvider,
} from 'assets/js/pages/engage/workflows/components/ParentMessageProvider';
import { StepFormContainer } from 'assets/js/pages/engage/workflows/components/StepFormContainer';
import { useDebouncedAutosave } from 'assets/js/pages/engage/workflows/hooks/useDebouncedAutosave';
import { useSyncFormToNode } from 'assets/js/pages/engage/workflows/hooks/useSyncFormToNode';
import { useTriggerValidationOnDeselection } from 'assets/js/pages/engage/workflows/hooks/useTriggerValidationOnDeselection';
import { baseNodeDefaults } from 'assets/js/pages/engage/workflows/utils/constants';
import { useReactFlowStore, workflowHasAction } from 'assets/js/pages/engage/workflows/utils/store';
import { formDefaults } from 'assets/js/pages/engage/workflows/utils/transformers';
import { NodeWithData } from 'assets/js/pages/engage/workflows/utils/types';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import { BranchConditionEnum, WorkflowUtilityNodeTypeEnum } from 'constants/workflows';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { twMerge } from 'tailwind-merge';
import { v4 as uuid } from 'uuid';

interface BranchActionNodeProps extends NodeProps<BranchNodeData> {}
const Branch = ({ id, selected, data: { formData } }: BranchActionNodeProps) => {
    const [isEditingCondition, setIsEditingCondition] = useState(!formData?.values.condition);
    const { workflowBranchIncludeAllTime } = useFlags();

    const { nodes, getNode, setNodes } = useReactFlowStore(({ nodes, getNode, setNodes }) => ({
        nodes,
        getNode,
        setNodes,
    }));

    const methods = Form.useGivebutterForm<BranchFormValues>({
        mode: 'all',
        defaultValues: {
            condition: formData?.values.condition,
            value: formData?.values.value,
            ...(workflowBranchIncludeAllTime && {
                include_all_time: formData?.values.include_all_time ?? false,
            }),
        },
    });
    const {
        control,
        formState: { errors },
        trigger,
        getValues,
        setValue,
        watch,
        path,
        clearErrors,
    } = methods;

    const [condition] = watch(['condition']);
    const isError = Object.keys(errors).length > 0;

    useSyncFormToNode<BranchFormValues>({ nodeId: id, control });
    useDebouncedAutosave<BranchFormValues>({ methods });
    useTriggerValidationOnDeselection<BranchFormValues>({ trigger, selected });

    const hasBranchConditions = nodes.filter(node => node.data?.parentId === id).length > 1;
    const nodeExists = !!getNode(id);

    const maybeInsertChildNodes = useCallback(() => {
        if (hasBranchConditions || !nodeExists) return;

        const yesUuid = uuid();
        const noUuid = uuid();
        const chooseActionNodeUuid = uuid();

        const branchLeafChild = nodes.find(node => node.data?.parentId === id);
        const nodesWithoutLeafEnd = nodes.filter(n => n.id !== branchLeafChild?.id);
        const hasActionNode = workflowHasAction(nodes);

        const chooseActionNode: NodeWithData = {
            ...baseNodeDefaults,
            id: chooseActionNodeUuid,
            type: WorkflowUtilityNodeTypeEnum.CHOOSE_ACTION,
            data: {
                type: WorkflowUtilityNodeTypeEnum.CHOOSE_ACTION,
                parentId: yesUuid,
            },
        };

        const newNodes = [
            ...nodesWithoutLeafEnd,
            {
                ...baseNodeDefaults,
                id: yesUuid,
                type: WorkflowUtilityNodeTypeEnum.BRANCH_CONDITION,
                selected: false,
                data: {
                    type: WorkflowUtilityNodeTypeEnum.BRANCH_CONDITION,
                    parentId: id,
                    formData: {
                        ...formDefaults,
                        values: {
                            value: true,
                        },
                    },
                } as BranchConditionNodeData,
            },
            ...(hasActionNode ? [] : [chooseActionNode]),
            {
                ...baseNodeDefaults,
                id: uuid(),
                data: {
                    type: WorkflowUtilityNodeTypeEnum.LEAF_END,
                    parentId: hasActionNode ? yesUuid : chooseActionNodeUuid,
                },
                type: WorkflowUtilityNodeTypeEnum.LEAF_END,
            },
            {
                ...baseNodeDefaults,
                id: noUuid,
                type: WorkflowUtilityNodeTypeEnum.BRANCH_CONDITION,
                selected: false,
                data: {
                    type: WorkflowUtilityNodeTypeEnum.BRANCH_CONDITION,
                    parentId: id,
                    formData: {
                        ...formDefaults,
                        values: {
                            value: false,
                        },
                    },
                } as BranchConditionNodeData,
            },
            {
                ...baseNodeDefaults,
                id: uuid(),
                data: {
                    type: WorkflowUtilityNodeTypeEnum.LEAF_END,
                    parentId: noUuid,
                },
                type: WorkflowUtilityNodeTypeEnum.LEAF_END,
            },
        ];
        setNodes(newNodes);
    }, [id, setNodes, nodes, hasBranchConditions, nodeExists]);

    const setDefaultFormFields = useCallback(
        (value: BranchConditionEnum) => {
            switch (value) {
                case BranchConditionEnum.CONTACTS_TAGS:
                    setValue('value.operator', 'any');
                    setValue('value.tags', []);
                    break;
                case BranchConditionEnum.MESSAGES_CLICK:
                case BranchConditionEnum.MESSAGES_OPEN:
                    setValue('value.messages', []);
                    break;
                case BranchConditionEnum.AUCTION_REGISTERED_TO_BID:
                case BranchConditionEnum.TICKET_PURCHASED:
                case BranchConditionEnum.DONATION_MADE:
                    setValue('value.campaigns', []);
                    break;
            }
        },
        [setValue],
    );

    const handleOnClickCondition = useCallback(
        (condition: BranchConditionEnum | null) => {
            setIsEditingCondition(false);
            setValue('condition', condition);
        },
        [setValue],
    );

    useEffect(() => {
        if (!selected && condition) {
            setIsEditingCondition(false);
        }
    }, [setIsEditingCondition, selected, condition]);

    return (
        <CanvasNode.Root className="max-w-96" isSelected={selected} isError={isError} id={id}>
            <ParentMessageProvider>
                <Form.Provider {...methods}>
                    <StepFormContainer>
                        <CanvasNode.Container>
                            <CanvasNode.ErrorAlert />
                            <CanvasNode.Header>
                                <CanvasNode.Icon
                                    icon={faSplit}
                                    className="text-brand-violet-primary rotate-90"
                                />
                                <div className="flex flex-col items-start">
                                    <CanvasNode.Title>Branch</CanvasNode.Title>
                                    <CanvasNode.Description>
                                        {selected
                                            ? 'Prepare different paths for your supporters based on your set condition.'
                                            : 'Supporter meets the condition:'}
                                    </CanvasNode.Description>
                                </div>
                                <DropdownMenu.Root>
                                    <CanvasNode.ActionMenuTrigger />
                                    <DropdownMenu.Content align="end">
                                        <DeleteCanvasNodeMenuItem />
                                    </DropdownMenu.Content>
                                </DropdownMenu.Root>
                            </CanvasNode.Header>
                            <CanvasNode.Body>
                                <CanvasNode.BodySelected>
                                    <Form.Container className="gap-2">
                                        <div
                                            className={twMerge(
                                                'flex items-center justify-between gap-2 rounded-lg border border-gray-200 p-3 px-4 font-medium text-gray-800',
                                                isEditingCondition && 'hidden',
                                            )}
                                        >
                                            <div className="flex items-center gap-2.5">
                                                <BranchConditionOptionLabel condition={condition} />
                                            </div>
                                            <Button
                                                variant="none"
                                                size="sm"
                                                onClick={() => {
                                                    setIsEditingCondition(true);
                                                    setValue('condition', null);
                                                    setValue('value', null);
                                                    trigger();
                                                }}
                                                aria-label={'Change condition'}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faTimes}
                                                    className="text-gray-600"
                                                />
                                            </Button>
                                        </div>

                                        <Form.UseFormRadioGroup
                                            control={control}
                                            name="condition"
                                            className={twMerge(!isEditingCondition && 'hidden')}
                                            onValueChange={(value: BranchConditionEnum) => {
                                                maybeInsertChildNodes();
                                                setValue('value', null);
                                                setDefaultFormFields(value);
                                                if (condition) {
                                                    clearErrors();
                                                }
                                            }}
                                            rules={{
                                                required: 'Branch condition is required',
                                            }}
                                            variant={'card'}
                                        >
                                            <BranchActionConditionItems
                                                onClick={() => handleOnClickCondition(condition)}
                                            />
                                        </Form.UseFormRadioGroup>
                                        <Form.Error name={path('condition')} />
                                    </Form.Container>
                                    {!isEditingCondition && <BranchFormValueInput />}
                                </CanvasNode.BodySelected>
                                <CanvasNode.BodyUnselected>
                                    <BranchActionFormValuesDisplay values={getValues()} />
                                </CanvasNode.BodyUnselected>
                            </CanvasNode.Body>
                        </CanvasNode.Container>
                    </StepFormContainer>
                </Form.Provider>
            </ParentMessageProvider>
        </CanvasNode.Root>
    );
};

interface BranchActionFormValuesDisplayProps {
    values: BranchFormValues;
}

const BranchActionFormValuesDisplay = ({
    values: { value, condition },
}: BranchActionFormValuesDisplayProps) => {
    return condition ? (
        <div className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3 px-4 font-medium text-gray-800">
            <div className="flex items-center gap-2">
                <BranchConditionOptionLabel condition={condition} />
            </div>

            <BranchMessageDisplayValue value={value} />
            <div className="ml-6">
                <BranchCampaignsDisplayValue value={value} />
                <BranchTagsDisplayValue value={value} />
            </div>
        </div>
    ) : (
        <div className="flex w-full justify-center rounded-lg border border-gray-200 p-2 text-sm font-medium">
            Set up condition
        </div>
    );
};

interface BranchActionConditionItemsProps {
    onClick: () => void;
}
const BranchActionConditionItems = ({ onClick }: BranchActionConditionItemsProps) => {
    const { messageOptions } = useContext(ParentMessageContext);
    const hasMessages = messageOptions.length > 0;

    return (
        <div className="flex w-full flex-col gap-2">
            <BranchConditionItem.Root
                onClick={() => onClick()}
                condition={BranchConditionEnum.CONTACTS_TAGS}
            />
            <BranchConditionItem.Root
                onClick={() => onClick()}
                condition={BranchConditionEnum.MESSAGES_OPEN}
                isDisabled={!hasMessages}
            >
                <BranchConditionItem.MessageTooltipContent />
            </BranchConditionItem.Root>
            <BranchConditionItem.Root
                onClick={() => onClick()}
                condition={BranchConditionEnum.MESSAGES_CLICK}
                isDisabled={!hasMessages}
            >
                <BranchConditionItem.MessageTooltipContent />
            </BranchConditionItem.Root>
            <BranchConditionItem.Root
                onClick={() => onClick()}
                condition={BranchConditionEnum.DONATION_MADE}
            />
            <BranchConditionItem.Root
                onClick={() => onClick()}
                condition={BranchConditionEnum.TICKET_PURCHASED}
            />
            <BranchConditionItem.Root
                onClick={() => onClick()}
                condition={BranchConditionEnum.AUCTION_REGISTERED_TO_BID}
            />
        </div>
    );
};

export { Branch, BranchActionFormValuesDisplay };
export type { BranchActionNodeProps };
